import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Trash2, Edit, ScanBarcode, Package, ShieldCheck, ShieldAlert, RotateCcw, Loader2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getSaleItemReturnDetails } from "@/lib/warrantyUtils";
import { extractItemSerials } from "@/lib/posSerialUtils";

const CartItem = ({
  product,
  currencySymbol,
  localQuantities,
  handleQuantityChange,
  updatingItemIds,
  handleEditCartItem,
  removeCartItem,
  cart,
  setCart,
  hasPermission,
  isSale = false,
  onClaimWarranty,
  duplicateSerials = [],
}) => {
  const returnDetails = getSaleItemReturnDetails(product);

  const serialNumbers = extractItemSerials(product);
  const hasDuplicateSerial = serialNumbers.some((sn) =>
    (duplicateSerials || []).some((d) => d.toLowerCase() === sn.toLowerCase())
  );

  const batchNumber =
    product.inventory_batch?.batch_number || product.batch_number;

  const productData =
    typeof product.product_data === "string"
      ? JSON.parse(product.product_data)
      : product.product_data || product.product || {};

  const trackingType = productData.manage_inventory
    ? productData.tracking_type ||
    (productData.is_serialized ? "serial" : "none")
    : "none";

  const isItemSyncing = Boolean(
    !product.id ||
    product._tempId ||
    product.isSyncing ||
    String(product.id).startsWith("temp-") ||
    (updatingItemIds && updatingItemIds.includes(product.id))
  );

  return (
    <div className="flex items-center justify-between p-2 border-b border-border last:border-0 hover:bg-muted/50 transition-colors">
      <div className="flex items-center gap-2 overflow-hidden">
        <img
          src={productData.media?.[0]?.url || "assets/img/product/noimage.png"}
          alt={productData.name}
          className="h-12 w-12 rounded-md object-cover border border-border"
        />
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground truncate">
              {productData.sku}
            </span>
          </div>
          <h6 className="font-medium text-sm truncate max-w-[150px]">
            <Link to="#" className="hover:text-primary transition-colors">
              {productData.name}
            </Link>
          </h6>
          {product.notes && (
            <p
              className="text-xs text-muted-foreground mt-0.5 line-clamp-2"
              title={product.notes}
            >
              {product.notes}
            </p>
          )}

          <div className="flex flex-col gap-1 mt-1">
            <p className="text-sm font-semibold text-primary">
              {currencySymbol}
              {parseFloat(product.unit_price).toFixed(2)}
            </p>

            {trackingType === "serial" && (
              <Badge
                variant="outline"
                className={`text-[10px] w-fit h-4 px-1 gap-1 ${hasDuplicateSerial
                  ? "bg-destructive/15 text-destructive border-destructive/40 font-semibold"
                  : returnDetails.isReturned
                    ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30"
                    : "bg-primary/5 text-blue-600 border-primary/20"
                  }`}
                title={
                  hasDuplicateSerial
                    ? "Duplicate serial number in cart - please remove"
                    : undefined
                }
              >
                <ScanBarcode size={10} />
                {serialNumbers.length > 0 ? (
                  <span className="truncate max-w-[100px]">
                    {serialNumbers[0]}
                  </span>
                ) : (
                  "0 Serials"
                )}
                {hasDuplicateSerial ? (
                  <span className="text-[9px] font-bold text-destructive ml-0.5">
                    (Duplicate)
                  </span>
                ) : returnDetails.isReturned ? (
                  <span className="text-[9px] font-semibold text-amber-600 dark:text-amber-400 ml-0.5">
                    ({returnDetails.label})
                  </span>
                ) : null}
              </Badge>
            )}

            {trackingType === "standard" &&
              (product.inventory_batch_id || product.batch_id) && (
                <Badge
                  variant="outline"
                  className="text-[10px] w-fit h-4 px-1 bg-blue-50 text-blue-700 border-blue-200 gap-1"
                >
                  <Package size={10} />
                  {batchNumber || "Batch Selected"}
                </Badge>
              )}
          </div>
        </div>
      </div>
      {isSale ? (
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {returnDetails.isReturned ? (
            <Badge
              variant="outline"
              className="text-[10px] h-5 py-0 px-2 font-semibold bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/40 gap-1"
              title={returnDetails.reason}
            >
              <RotateCcw size={10} className="text-amber-600 dark:text-amber-400" />
              <span>{returnDetails.label || "Returned"}</span>
            </Badge>
          ) : product.warranty_end_date ? (
            <Badge
              variant="outline"
              className={`text-[10px] h-5 py-0 px-1.5 font-medium ${new Date(product.warranty_end_date) >= new Date()
                ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"
                : "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/30"
                }`}
            >
              {new Date(product.warranty_end_date) >= new Date()
                ? "Active Warranty"
                : "Expired"}
            </Badge>
          ) : null}

          {onClaimWarranty && hasPermission && hasPermission("warranty claim create") && (
            returnDetails.isReturned ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled
                        className="h-7 px-2 text-xs opacity-50 cursor-not-allowed text-muted-foreground border-dashed gap-1 font-medium"
                      >
                        <ShieldAlert size={13} className="text-amber-600" />
                        <span>Claimed</span>
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{returnDetails.reason || "This item was already returned / claimed."}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs text-purple-700 dark:text-purple-300 border-purple-500/30 hover:bg-purple-500/10 gap-1 font-medium cursor-pointer"
                onClick={() => onClaimWarranty(product)}
                title="Create Warranty Claim for this item"
              >
                <ShieldCheck size={13} className="text-purple-600" />
                <span>Claim</span>
              </Button>
            )
          )}

          <Badge
            variant="secondary"
            className="font-semibold text-xs h-7 px-2 bg-muted text-foreground border border-border/60"
          >
            Qty: {localQuantities?.[product.id] !== undefined ? localQuantities[product.id] : (product.quantity || 1)}
          </Badge>
          <div className="text-right min-w-[70px]">
            <div className="text-xs font-bold tabular-nums text-foreground">
              {currencySymbol}
              {parseFloat(
                product.line_total != null
                  ? product.line_total
                  : (product.quantity || 1) * (product.unit_price || 0),
              ).toFixed(2)}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          {/* Quantity Input */}
          <div className="w-[75px]">
            <Input
              type="number"
              className="h-8 text-base text-center px-1 font-semibold"
              min="1"
              value={
                localQuantities[product.id] !== undefined
                  ? localQuantities[product.id]
                  : product.quantity
              }
              onChange={(e) => handleQuantityChange(product, e.target.value)}
              disabled={
                isItemSyncing || trackingType !== "none"
              }
              title={
                isItemSyncing
                  ? "Item is syncing with server..."
                  : trackingType !== "none"
                    ? "Items with Batch/Serial tracking must be edited via the modal"
                    : ""
              }
            />
          </div>

          {/* Edit button */}
          {(hasPermission?.(isSale ? "sale item update" : "cart item update") ||
            hasPermission?.("sale item update")) && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-primary shrink-0 cursor-pointer disabled:cursor-not-allowed"
                        disabled={isItemSyncing}
                        onClick={() => !isItemSyncing && handleEditCartItem(product)}
                        title={isItemSyncing ? "Syncing item with server..." : "Edit Item"}
                      >
                        {isItemSyncing ? (
                          <Loader2 className="h-4 w-4 animate-spin text-primary" />
                        ) : (
                          <Edit size={16} />
                        )}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{isItemSyncing ? "Syncing with cart..." : "Edit Item"}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

          {/* Delete button */}
          {(hasPermission?.(isSale ? "sale item delete" : "cart item delete") ||
            hasPermission?.("sale item delete")) && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0 cursor-pointer disabled:cursor-not-allowed"
                        disabled={isItemSyncing}
                        onClick={() => {
                          if (isItemSyncing) return;
                          setCart({
                            ...cart,
                            item_count: cart.item_count - product.quantity,
                          });
                          removeCartItem(product.id);
                        }}
                        title={isItemSyncing ? "Syncing item with server..." : "Remove Item"}
                      >
                        {isItemSyncing ? (
                          <Loader2 className="h-4 w-4 animate-spin text-destructive" />
                        ) : (
                          <Trash2 size={16} className={isItemSyncing ? "opacity-30" : ""} />
                        )}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{isItemSyncing ? "Syncing with cart..." : "Remove Item"}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
        </div>
      )}
    </div>
  );
};

CartItem.propTypes = {
  product: PropTypes.object.isRequired,
  currencySymbol: PropTypes.string,
  localQuantities: PropTypes.object,
  handleQuantityChange: PropTypes.func,
  updatingItemIds: PropTypes.array,
  handleEditCartItem: PropTypes.func,
  removeCartItem: PropTypes.func,
  cart: PropTypes.object,
  setCart: PropTypes.func,
  hasPermission: PropTypes.func,
  isSale: PropTypes.bool,
  onClaimWarranty: PropTypes.func,
  duplicateSerials: PropTypes.array,
};

export default CartItem;
